import React, { useEffect, useState } from 'react'
import Modal, { ModalProps } from 'components/modal'
import Button from 'components/button'
import api from 'helpers/api'
import { IUserObject, IApiKey, IProject, ITable, IProcess, ISite } from 'types'
import { Copy, Plus } from 'components/icons'
import { useApplicationStore } from 'hooks/application'
import Select from 'components/select'
import { PROJECT } from 'app-constants'
import { capitaliseFirstLetter } from 'helpers/utils'
import Input from 'components/input'

interface KeyModalProps extends ModalProps {
  user: IUserObject
  setUserData: (user: IUserObject) => void
  mode: 'new' | 'edit'
  initialApiKey: IApiKey
}

const KeyModal: React.FC<KeyModalProps> = ({ id, open, setOpen, onClose, setUserData, mode, initialApiKey, user }) => {
  const { displayErrorMessage } = useApplicationStore()

  const [apiKey, setAPIKey] = useState<IApiKey>(initialApiKey)
  const [stage, setStage] = useState(mode === 'new' ? { key: '', number: 0 } : null)

  const [sites, setSites] = useState<ISite[]>([])
  const [loadingSites, setLoadingSites] = useState<boolean>(false)

  const [projects, setProjects] = useState<IProject[]>([])
  const [fetchedProjects, setFetchedProjects] = useState<string[]>([])
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false)

  const [tables, setTables] = useState<ITable[]>([])
  const [documents, setDocuments] = useState<IProcess[]>([])
  const [loadingResources, setLoadingResources] = useState<boolean>(false)

  // Filter projects based on selected site restrictions
  const filteredProjects =
    apiKey.siteRestrictions && apiKey.siteRestrictions.length > 0
      ? projects.filter((project) => project.site?.publicId && apiKey.siteRestrictions!.includes(project.site.publicId))
      : projects

  useEffect(() => {
    setLoadingProjects(true)
    setLoadingSites(true)
    api
      .getOwnerProjects()
      .then((response) => {
        setProjects(response.data)
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
      .finally(() => {
        setLoadingProjects(false)
      })
    api
      .getUserSites()
      .then((response) => {
        setSites(response.data)
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
      .finally(() => {
        setLoadingSites(false)
      })
  }, [])

  const getProjectResources = async (projectPublicId: string) => {
    try {
      const tablesResponse = await api.getProjectTables(projectPublicId)
      const documentsResponse = await api.getProjectProcesses(projectPublicId)
      return { tables: tablesResponse.data, documents: documentsResponse.data }
    } catch (error) {
      displayErrorMessage(error)
      return { tables: [], documents: [] }
    }
  }

  useEffect(() => {
    setLoadingResources(true)
    if (apiKey.projectRestrictions) {
      for (const project of apiKey.projectRestrictions) {
        if (!fetchedProjects.includes(project)) {
          getProjectResources(project).then((response) => {
            setTables(tables.concat(response.tables))
            setDocuments(documents.concat(response.documents))
            setFetchedProjects([...fetchedProjects, project])
          })
        }
      }
    }
    setLoadingResources(false)
  }, [apiKey.projectRestrictions])

  async function handleSubmit(): Promise<null | undefined> {
    if (!apiKey || apiKey.name === '') return null

    if (mode === 'new') {
      try {
        const response = await api.createApiKey(apiKey)
        setStage({ key: response.data.tempKey, number: 1 })
        setUserData({
          ...user,
          apiKeys: [...user.apiKeys, response.data]
        })
      } catch (error) {
        displayErrorMessage(error)
      }
    } else {
      try {
        await api.updateApiKey(apiKey)
        setUserData({
          ...user,
          apiKeys: user.apiKeys.map((key) => {
            if (key.hash === apiKey.hash) {
              return apiKey
            }
            return key
          })
        })
        if (onClose) onClose()
        setOpen(false)
      } catch (error) {
        displayErrorMessage(error)
      }
    }
  }

  // Only used in new mode once a key is generated.
  function handleSave(): void {
    setAPIKey({
      name: 'New API Key',
      accessLevel: 1,
      siteRestrictions: [],
      projectRestrictions: [],
      tableRestrictions: [],
      documentRestrictions: [],
      hash: '',
      publicId: '',
      prefix: ''
    })
    setStage({ key: '', number: 0 })
    setOpen(false)
  }

  const renderForm = () => (
    <div>
      <div className="flex flex-col mb-8 gap-5">
        <div className="font-bold">API Key Name</div>
        <p>This should be a name that will remind you what the API key is for.</p>
        <Input
          data-cy="api-key-name"
          placeholder="e.g. PowerBI Dashboard"
          required
          value={apiKey.name}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            setAPIKey({ ...apiKey, name: event.target.value })
          }
        />
      </div>

      <div className="flex flex-col mb-8 gap-5">
        <div className="font-bold">Read + Write Restriction</div>
        <p>
          You can restrict this API key to only have read or write access. If you are unsure, select both. You can
          always change this later.
        </p>
        <Select
          options={[
            { value: '0', label: 'Read' },
            { value: '1', label: 'Read + Write' }
          ]}
          optionsSelected={apiKey.accessLevel !== undefined ? [apiKey.accessLevel.toString()] : []}
          onOptionClick={(value: string): void => setAPIKey({ ...apiKey, accessLevel: Number.parseInt(value) })}
        />
      </div>

      <div className="flex flex-col mb-8 gap-5">
        <div className="font-bold">Site Restriction</div>
        <p>
          You can restrict this API key to only have access to particular sites. If you are unsure, leave blank
          (allowing access to all).
        </p>
        <Select
          multiselect={true}
          options={sites.map((site) => ({
            value: site.publicId,
            label: site.name,
            group: site.organisation?.name
          }))}
          optionsSelected={apiKey.siteRestrictions || []}
          loading={loadingSites}
          groupBy={true}
          onOptionClick={(value: string) => {
            if (apiKey.siteRestrictions?.includes(value)) {
              // Get project IDs for the site being removed
              const projectIdsForSite = new Set(
                projects.filter((project) => project.site?.publicId === value).map((project) => project.publicId)
              )

              // Get table and document IDs for projects in this site
              const tableIdsForSite = new Set(
                tables
                  ?.filter((table) => projectIdsForSite.has(table.projectPublicId))
                  .map((table) => table.publicId) ?? []
              )

              const documentIdsForSite = new Set(
                documents
                  ?.filter((document) => projectIdsForSite.has(document.projectPublicId))
                  .map((document) => document.publicId) ?? []
              )

              setAPIKey({
                ...apiKey,
                siteRestrictions: apiKey.siteRestrictions.filter((siteId) => siteId !== value),
                projectRestrictions: apiKey.projectRestrictions.filter(
                  (projectId) => !projectIdsForSite.has(projectId)
                ),
                tableRestrictions: apiKey.tableRestrictions.filter((tableId) => !tableIdsForSite.has(tableId)),
                documentRestrictions: apiKey.documentRestrictions.filter(
                  (documentId) => !documentIdsForSite.has(documentId)
                )
              })

              // Clean up fetched projects for this site
              setFetchedProjects(fetchedProjects.filter((projectId) => !projectIdsForSite.has(projectId)))
              setTables(tables?.filter((table) => !projectIdsForSite.has(table.projectPublicId)))
              setDocuments(documents?.filter((document) => !projectIdsForSite.has(document.projectPublicId)))
            } else {
              setAPIKey({
                ...apiKey,
                siteRestrictions: [...(apiKey.siteRestrictions || []), value]
              })
            }
          }}
        />
      </div>

      <div className="flex flex-col mb-8 gap-5">
        <div className="font-bold">{capitaliseFirstLetter(PROJECT)} Restriction</div>
        <p>
          You can restrict this API key to only have access to particular {PROJECT}s. If you are unsure, leave blank
          (allowing access to all).
        </p>
        <Select
          multiselect={true}
          options={filteredProjects.map((project) => ({
            value: project.publicId,
            label: project.name,
            group: project.site ? project.site.name : 'No Site'
          }))}
          optionsSelected={apiKey.projectRestrictions || []}
          loading={loadingProjects}
          groupBy={true}
          onOptionClick={(value: string) => {
            if (apiKey.projectRestrictions.includes(value)) {
              // Tables & docs for the project being removed
              const tableIdsForProject = new Set(
                tables?.filter((table) => table.projectPublicId === value).map((table) => table.publicId) ?? []
              )

              const documentIdsForProject = new Set(
                documents
                  ?.filter((document) => document.projectPublicId === value)
                  .map((document) => document.publicId) ?? []
              )

              setAPIKey({
                ...apiKey,
                projectRestrictions: apiKey.projectRestrictions.filter((projectId) => projectId !== value),
                tableRestrictions: apiKey.tableRestrictions.filter((tableId) => !tableIdsForProject.has(tableId)),
                documentRestrictions: apiKey.documentRestrictions.filter(
                  (documentId) => !documentIdsForProject.has(documentId)
                )
              })

              setFetchedProjects(fetchedProjects.filter((projectId) => projectId !== value))
              setTables(tables?.filter((table) => table.projectPublicId !== value))
              setDocuments(documents?.filter((document) => document.projectPublicId !== value))
            } else {
              setAPIKey({
                ...apiKey,
                projectRestrictions: [...apiKey.projectRestrictions, value]
              })
            }
          }}
        />
      </div>

      <div className="flex flex-col mb-8 gap-5">
        <div className="font-bold">Document Restriction</div>
        <p>
          You can restrict this API key to only have access to particular documents. If you are unsure, leave blank
          (allowing access to all).
        </p>
        <Select
          multiselect={true}
          options={
            documents
              ? documents.map((document) => ({
                  value: document.publicId,
                  label: document.name,
                  group: document.projectName
                }))
              : []
          }
          optionsSelected={apiKey.documentRestrictions}
          onOptionClick={(value: string) => {
            if (apiKey.documentRestrictions.includes(value)) {
              setAPIKey({
                ...apiKey,
                documentRestrictions: apiKey.documentRestrictions.filter((doc) => doc !== value)
              })
            } else {
              setAPIKey({ ...apiKey, documentRestrictions: [...apiKey.documentRestrictions, value] })
            }
          }}
          loading={loadingResources}
          groupBy={true}
        />
      </div>

      <div className="flex flex-col mb-8 gap-5">
        <div className="font-bold">Table Restriction</div>
        <p>
          You can restrict this API key to only have access to particular tables. If you are unsure, leave blank
          (allowing access to all).
        </p>
        <Select
          multiselect={true}
          options={
            tables
              ? tables.map((table) => ({
                  value: table.publicId,
                  label: table.name,
                  group: table.projectName
                }))
              : []
          }
          optionsSelected={apiKey.tableRestrictions}
          onOptionClick={(value: string) => {
            if (apiKey.tableRestrictions.includes(value)) {
              setAPIKey({
                ...apiKey,
                tableRestrictions: apiKey.tableRestrictions.filter((tbl) => tbl !== value)
              })
            } else {
              setAPIKey({ ...apiKey, tableRestrictions: [...apiKey.tableRestrictions, value] })
            }
          }}
          groupBy={true}
          loading={loadingResources}
        />
      </div>

      <div className="flex justify-end mt-5">
        <Button
          data-cy={mode === 'new' ? 'generate-new-api-key' : 'api-key-save'}
          internalType="accept"
          className="text-base mt-10"
          onClick={() => handleSubmit()}
        >
          {mode === 'new' ? (
            <>
              <Plus className="mr-[5px]" />
              Generate New API Key
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </div>
  )

  return (
    <Modal
      id={id}
      open={open}
      setOpen={setOpen}
      onClose={onClose}
      title={mode === 'new' ? 'New API Key' : 'Update API Key'}
    >
      {mode === 'new' && stage && stage.number === 1 ? (
        <div>
          <p>
            Here is your API key – please make sure you save this in a safe place as we do not store your API key for
            security reasons.
          </p>
          <div className="flex flex-row items-center rounded shadow-heavy w-full p-2.5 flex-wrap my-8 border border-divider-strong select-none">
            <div data-cy="generated-api-key">{stage.key}</div>
            <span
              onClick={(): Promise<void> => navigator.clipboard.writeText(stage.key)}
              className="cursor-pointer ml-auto"
            >
              <Copy />
            </span>
          </div>
          <div className="flex justify-end mt-5">
            <Button data-cy="saved-key" onClick={handleSave}>
              I Have Saved My Key
            </Button>
          </div>
        </div>
      ) : (
        renderForm()
      )}
    </Modal>
  )
}

export default KeyModal
